import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Switch, Route, useHistory } from 'react-router-dom';
import { Box } from '@mui/material';
import {
  DashboardOutlined,
  Dashboard as DashboardIcon,
  GroupOutlined,
  Home,
  HomeOutlined,
  MenuBookOutlined,
  Group,
  MenuBook,
} from '@mui/icons-material';
import { useAccount } from '../contexts/Account';
import Dashboard from '../scenes/AdvisorDashboardV2';
import IndividualPost from '../scenes/IndividualPost';
import AdvisorAccount from '../scenes/AdvisorAccount';
import Networking from '../scenes/Networking';
import TopBar from '../components/TopBar';
import AdvisoryBoards from '../scenes/AdvisoryBoards';
import Boardroom from '../scenes/Boardroom';
import PersonalAdvisoryBoards from '../scenes/PersonalAdvisoryBoards';
import CustomIFrameScene from '../scenes/CustomIFrameScene';
import { getAdvisorQueryParams } from '../utils/queryParams';
import DynamicIFrameScene from '../scenes/DynamicIFrameScene';
import MobileBottomNav from '../components/TopBar/MobileBottomNav';

const useStyles = () => ({
  baseContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    marginTop: '0px',
    backgroundColor: '#f4f8ff',
    overscrollBehavior: 'contain',
    marginBottom: { xxs: '56px', xs: 0 },
  },
  impersonationBanner: {
    position: 'fixed',
    width: '100%',
    zIndex: 1000,
  },
  contentContainer: {
    flex: 'auto',
    display: 'flex',
    flexDirection: 'row',
    minHeight: '100dvh',
  },
  mainViewContainer: {
    flex: 'auto',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
  },
  viewContainer: {
    display: 'flex',
    flex: 'auto',
    overflowX: 'hidden',
  },
});

function Layout() {
  const {
    isImpersonated,
    accountDetails,
    isFreeTrial,
    isFreemium,
  } = useAccount();
  const history = useHistory();
  const classes = useStyles();
  const topBarRef = useRef(null);
  const containerRef = useRef(null);
  const [retry, setRetry] = useState(true);

  function getContentMarginTop() {
    try {
      const topBarNode = ReactDOM.findDOMNode(topBarRef.current);
      const bodyContainerNode = ReactDOM.findDOMNode(containerRef.current);
      const topBarRect = topBarNode.children[0].getBoundingClientRect();
      bodyContainerNode.style.paddingTop = `${topBarRect.height}px`;
    } catch (error) {
      console.log(error);
      if (retry) {
        setRetry(false);
        setTimeout(() => {
          getContentMarginTop();
        }, 20);
      }
    }
  }

  const menuItems = [
    {
      label: 'Dashboard',
      to: '/dashboard',
      id: 'menu-dashboard',
      icon: <HomeOutlined />,
      filledIcon: <Home />,
    },
    {
      label: 'Advisory Boards',
      shortLabel: 'Boards',
      to: '/board-directory',
      id: 'menu-join-advisory-boards',
      paths: ['/board-directory', '/peer-directory'],
      icon: <DashboardOutlined />,
      filledIcon: <DashboardIcon />,
    },
    {
      label: 'Networking',
      id: 'menu-networking',
      to: '/networking',
      icon: <GroupOutlined />,
      filledIcon: <Group />
    },
    {
      label: 'Resources',
      id: 'menu-tools-resources',
      to: '/tools-resources',
      onClick: () => {
        history.push('/tools-resources');
      },
      icon: <MenuBookOutlined />,
      filledIcon: <MenuBook />,
    },
  ];

  useEffect(() => {
    getContentMarginTop();
  }, [topBarRef, containerRef]);

  useEffect(() => {
    const handleTouchStart = (event) => {
      if (event.touches.length === 1) {
        window._startY = event.touches[0].clientY;
      }
    };

    const handleTouchMove = (event) => {
      const startY = window._startY || 0;
      const currentY = event.touches[0].clientY;
      const scrollable = event.target.closest('.not-scrollable');
      const scrollTop = scrollable ? scrollable.scrollTop : window.scrollY;
      if (scrollTop === 0 && currentY > startY && !scrollable) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const FAQsComponent = () => (
    <CustomIFrameScene
      title="Resources"
      url={
        'https://register.advisorycloud.com/platform/jb-faqs/b.html?' +
        getAdvisorQueryParams({
          accountDetails,
          isFreeTrial,
          isFreemium,
        })
      }
    />
  );

  const ToolsComponent = () => (
    <CustomIFrameScene
      title="Tools & Resources"
      url={
        'https://register.advisorycloud.com/platform/jb-tools-1/?' +
        getAdvisorQueryParams({
          accountDetails,
          isFreeTrial,
          isFreemium,
        })
      }
    />
  );

  const UpgradeComponent = () => (
    <CustomIFrameScene
      title="Upgrades & Services"
      url={
        'https://register.advisorycloud.com/platform/special-offers/?' +
        getAdvisorQueryParams({
          accountDetails,
          isFreeTrial,
          isFreemium,
        })
      }
    />
  );

  const StoriesComponent = () => (
    <CustomIFrameScene
      title="Success Stories"
      url={
        'https://register.advisorycloud.com/platform/success-stories/?' +
        getAdvisorQueryParams({
          accountDetails,
          isFreeTrial,
          isFreemium,
        })
      }
    />
  );

  return (
    <Box sx={classes.baseContainer}>
      <Box ref={topBarRef}>
        <TopBar menuItems={menuItems} />
      </Box>
      <MobileBottomNav menuItems={menuItems} />
      {isImpersonated && (
        <div style={classes.impersonationBanner}>
          {/* Aquí podrías poner el componente de la banner de impersonation si existe */}
        </div>
      )}
      <div style={classes.contentContainer} ref={containerRef}>
        <div style={classes.mainViewContainer}>
          <Box sx={classes.viewContainer}>
            <Switch>
              <Route path="/dashboard" component={Dashboard} />
              <Route path="/post/:postId?" component={IndividualPost} />
              <Route path="/account" component={AdvisorAccount} />
              <Route path="/:companySlug/boardroom" component={Boardroom} />
              <Route path="/account" component={AdvisorAccount} />
              <Route
                path="/personal-advisory-boards"
                component={PersonalAdvisoryBoards}
              />

              <Route path="/board-directory" component={AdvisoryBoards} />
              <Route path="/peer-directory" component={AdvisoryBoards} />
              <Route path="/faqs" component={FAQsComponent} />
              <Route path="/tools-resources" component={ToolsComponent} />
              <Route path="/networking" component={Networking} />
              <Route
                path="/upgrade-and-services"
                component={UpgradeComponent}
              />
              <Route path="/success-stories" component={StoriesComponent} />
              <Route path="/ub" component={DynamicIFrameScene} />
              <Route path="/wf" component={DynamicIFrameScene} />
              <Route path="/messages" component={Dashboard} />
            </Switch>
          </Box>
        </div>
      </div>
    </Box>
  );
}

export default Layout;
